import { useEffect, useState } from 'react';
import type { SphereId } from '@superposition/domain';
import { StoreProvider, useStore } from '../lib/store.js';
import { hideBackButton, onBackButton, openTelegramLink, signalReady } from '../lib/telegram.js';
import { WheelScreen } from '../features/wheel/WheelScreen.js';
import { MatrixScreen } from '../features/matrix/MatrixScreen.js';
import { ObserverScreen } from '../features/observer/ObserverScreen.js';
import { LakeScreen } from '../features/lake/LakeScreen.js';
import { MethodScreen } from '../features/method/MethodScreen.js';
import { SettingsSheet } from '../features/settings/SettingsSheet.js';
import { useDocumentLang, useT } from '../i18n/index.js';

type ScreenId = 'wheel' | 'matrix' | 'obs' | 'lake' | 'method';

const NAV: { id: ScreenId; icon: React.ReactNode }[] = [
  {
    id: 'wheel',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 3v18M3 12h18M6 6l12 12M18 6L6 18" />
      </svg>
    ),
  },
  {
    id: 'matrix',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
        <path d="M14 17.5l2 2 4-4.5" />
      </svg>
    ),
  },
  {
    id: 'obs',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" />
        <circle cx="12" cy="12" r="2.6" />
      </svg>
    ),
  },
  {
    id: 'lake',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M3 15c2-2 4 2 6 0s4-2 6 0 4 2 6 0" />
        <path d="M3 19c2-2 4 2 6 0s4-2 6 0 4 2 6 0" />
        <path d="M12 3v6M9 6l3-3 3 3" />
      </svg>
    ),
  },
  {
    id: 'method',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H19v15H6.5A2.5 2.5 0 0 0 4 20.5z" />
        <path d="M4 20.5A2.5 2.5 0 0 1 6.5 18H19v3H6.5A2.5 2.5 0 0 1 4 20.5z" />
        <path d="M8 7.5h7M8 11h5" />
      </svg>
    ),
  },
];

export function App(): React.ReactNode {
  useDocumentLang();
  useEffect(() => {
    signalReady();
  }, []);

  return (
    <StoreProvider>
      <Shell />
    </StoreProvider>
  );
}

function Shell(): React.ReactNode {
  const store = useStore();
  const t = useT();
  const [screen, setScreen] = useState<ScreenId>('wheel');
  // глава, на которую увёл наблюдатель: «метод» открывает её и сбрасывает метку
  const [methodChapter, setMethodChapter] = useState<string | null>(null);
  const [methodAnchor, setMethodAnchor] = useState<string | null>(null);
  const [lakeSphere, setLakeSphere] = useState<SphereId | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // BackButton возвращает на колесо с любого другого экрана
  useEffect(() => {
    if (screen === 'wheel') {
      hideBackButton();
      return;
    }
    return onBackButton(() => setScreen('wheel'));
  }, [screen]);

  if (store.phase.kind === 'loading') {
    return (
      <div className="gate">
        <h1>{t.common.brand}</h1>
        <p>{t.app.checkingSession}</p>
      </div>
    );
  }

  if (store.phase.kind === 'no_telegram') {
    return (
      <div className="gate">
        <h1>{t.common.brand}</h1>
        <p>{t.app.noTelegram}</p>
        <p style={{ fontSize: 13, color: 'var(--muted2)' }}>{t.app.noTelegramBrowser}</p>
      </div>
    );
  }

  if (store.phase.kind === 'no_access') {
    const { telegramId, expiredAt, renewUrl, buyUrl } = store.phase;
    const handle = /^https:\/\/t\.me\/([A-Za-z0-9_]+)\/?$/.exec(renewUrl)?.[1];
    return (
      <div className="gate gate-access">
        <h1>{expiredAt ? t.app.accessExpiredTitle : t.app.accessInviteTitle}</h1>
        <p>{expiredAt ? t.app.accessExpiredText : t.app.accessInviteText}</p>
        {buyUrl ? (
          <>
            <p>{t.app.accessInBot}</p>
            <button type="button" onClick={() => openTelegramLink(buyUrl)}>
              {expiredAt ? t.app.renewAccess : t.app.getAccess}
            </button>
          </>
        ) : (
          <>
            <p>{t.app.writeTo({ handle: handle ?? null })}</p>
            <div className="access-id" aria-label={t.app.telegramIdAria}>
              {telegramId}
            </div>
            <button type="button" onClick={() => openTelegramLink(renewUrl)}>
              {expiredAt ? t.app.renewAccess : t.app.writeKate}
            </button>
          </>
        )}
        <p className="access-retry">
          <button type="button" className="link" onClick={() => void store.signIn()}>
            {t.app.accessRetry}
          </button>
        </p>
      </div>
    );
  }

  if (store.phase.kind === 'auth_failed') {
    return (
      <div className="gate">
        <h1>{t.app.authFailedTitle}</h1>
        <p>{store.phase.message}</p>
        <button type="button" onClick={() => void store.signIn()}>
          {t.app.tryAgain}
        </button>
      </div>
    );
  }

  if (!store.state) return null;

  return (
    <>
      <div className="app">
        <header>
          <div className="brand">
            <svg className="mark" viewBox="0 0 44 24" aria-hidden="true">
              <circle cx="14" cy="12" r="9" />
              <circle cx="22" cy="12" r="9" />
              <circle cx="30" cy="12" r="9" />
            </svg>
            {t.common.brand}<span className="dot">.</span>
          </div>
          <div className="today">{t.common.formatDay(store.today)}</div>
          <button type="button" className="gear" aria-label={t.app.settingsAria} onClick={() => setSettingsOpen(true)}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="3.2" />
              <path d="M12 2.8v2.4M12 18.8v2.4M21.2 12h-2.4M5.2 12H2.8M18.5 5.5l-1.7 1.7M7.2 16.8l-1.7 1.7M18.5 18.5l-1.7-1.7M7.2 7.2 5.5 5.5" />
            </svg>
          </button>
        </header>
        {settingsOpen ? <SettingsSheet onClose={() => setSettingsOpen(false)} /> : null}

        <SaveBar />

        {screen === 'wheel' ? <WheelScreen /> : null}
        {screen === 'matrix' ? <MatrixScreen /> : null}
        {screen === 'obs' ? (
          <ObserverScreen
            onOpenMethod={(id, anchor) => {
              setMethodChapter(id);
              setMethodAnchor(anchor ?? null);
              setScreen('method');
              window.scrollTo(0, 0);
            }}
            onOpenLake={(sphere) => {
              setLakeSphere(sphere);
              setScreen('lake');
              window.scrollTo(0, 0);
            }}
          />
        ) : null}
        {screen === 'lake' ? <LakeScreen initialSphere={lakeSphere} /> : null}
        {screen === 'method' ? (
          <MethodScreen openId={methodChapter} openAnchor={methodAnchor} onOpened={() => setMethodChapter(null)} />
        ) : null}
      </div>

      <nav>
        <div className="nav-in">
          {NAV.map((n) => (
            <button
              type="button"
              key={n.id}
              className={`nav-b${screen === n.id ? ' active' : ''}`}
              aria-current={screen === n.id ? 'page' : undefined}
              onClick={() => {
                setLakeSphere(null);
                setScreen(n.id);
                window.scrollTo(0, 0);
              }}
            >
              {n.icon}
              {t.app.nav[n.id]}
            </button>
          ))}
        </div>
      </nav>
    </>
  );
}

/** статус сохранения показывается явно: «сохранено» — только после ответа сервера */
function SaveBar(): React.ReactNode {
  const store = useStore();
  const t = useT();
  const [draftOpen, setDraftOpen] = useState(false);
  const s = store.save;

  const text =
    s.kind === 'clean'
      ? ''
      : s.kind === 'pending'
        ? t.save.pending
        : s.kind === 'saving'
          ? t.common.saving
          : s.kind === 'saved'
            ? t.common.saved
            : s.message;

  if (s.kind === 'clean') return <div className="savebar" aria-live="polite" />;

  return (
    <>
      <div className={`savebar ${s.kind}`} aria-live="polite">
        <span className="led" />
        <span>{text}</span>
        {s.kind === 'failed' && s.canRetry ? (
          <button type="button" onClick={store.retry}>
            {t.save.retry}
          </button>
        ) : null}
        {s.kind === 'failed' || s.kind === 'conflict' ? (
          <button type="button" onClick={() => setDraftOpen(true)}>
            {t.save.draft}
          </button>
        ) : null}
        {s.kind === 'conflict' ? (
          <button type="button" onClick={() => {
            if (window.confirm(t.save.confirmReload)) {
              void store.reload().catch(() => window.alert(t.save.reloadFailed));
            }
          }}>
            {t.save.takeServer}
          </button>
        ) : null}
      </div>

      {draftOpen ? (
        <div className="sheet" role="dialog" aria-modal="true" aria-labelledby="draft-title">
          <div className="sheet-in">
            <h3 id="draft-title">{t.save.draftTitle}</h3>
            <p>{t.save.draftText}</p>
            <textarea readOnly value={store.draftJson()} style={{ width: '100%', height: 180 }} />
            <div className="sheet-actions">
              <button
                type="button"
                onClick={() => {
                  const blob = new Blob([store.draftJson()], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `superpozitsiya-chernovik-${store.today}.json`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  setTimeout(() => URL.revokeObjectURL(url), 1500);
                }}
              >
                {t.save.download}
              </button>
              <button type="button" className="primary" onClick={() => setDraftOpen(false)}>
                {t.common.close}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
